import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, doc, updateDoc, getDoc, increment } from 'firebase/firestore';
import { firestore } from '../../firebase';
import Header from './components/Header';
import Footer from './components/Footer';

const Alert = ({ type, title, children }) => {
    const getAlertStyles = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50 text-green-800 border-green-200';
            case 'error':
                return 'bg-red-50 text-red-800 border-red-200';
            default:
                return 'bg-blue-50 text-blue-800 border-blue-200';
        }
    };

    return (
        <div className={`p-4 rounded-lg border ${getAlertStyles()}`}>
            {title && <div className="font-medium mb-1">{title}</div>}
            <div className="text-sm">{children}</div>
        </div>
    );
};

const TicketsListPage = () => {
    const [tickets, setTickets] = useState([]);
    const [theatres, setTheatres] = useState([]);
    const [shows, setShows] = useState([]);
    const [screens, setScreens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionStatus, setActionStatus] = useState({ message: '', type: '' });
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [filters, setFilters] = useState({
        paymentStatus: '',
        district: '',
        theatreId: '',
        screenName: '',
        showDate: '',
    });
    const [stats, setStats] = useState({
        total: 0,
        refunded: 0,
        confirmed: 0,
        pendingTicket: 0,
    });

    useEffect(() => {
        fetchTheatres();
        fetchTickets();
    }, []);

    useEffect(() => {
        if (filters.theatreId) {
            fetchScreens();
            if (filters.screenName && filters.showDate) {
                fetchShows();
            }
        }
    }, [filters.theatreId, filters.screenName, filters.showDate]);

    const fetchTheatres = async () => {
        try {
            const theatresRef = collection(firestore, 'theatres');
            const theatresSnapshot = await getDocs(theatresRef);
            const theatresList = theatresSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTheatres(theatresList);
        } catch (err) {
            console.error('Error fetching theatres:', err);
        }
    };

    const fetchScreens = async () => {
        try {
            const showsRef = collection(firestore, 'shows');
            const showsQuery = query(showsRef, where('theatre-id', '==', filters.theatreId));
            const showsSnapshot = await getDocs(showsQuery);
    
            const uniqueScreens = [...new Set(showsSnapshot.docs.map(doc => doc.data()['screen-name']))];
            setScreens(uniqueScreens);
        } catch (err) {
            console.error('Error fetching screens:', err);
        }
    };

    const fetchShows = async () => {
        try {
            const showsRef = collection(firestore, 'shows');
            let showsQuery = query(
                showsRef,
                where('theatre-id', '==', filters.theatreId),
                where('screen-name', '==', filters.screenName)
            );
    
            const showsSnapshot = await getDocs(showsQuery);
            const showsList = showsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
    
            // Filter shows by date if date is selected
            const filteredShows = filters.showDate ? showsList.filter(show => {
                const showDate = new Date(show['show-date'].toDate()).toLocaleDateString();
                const filterDate = new Date(filters.showDate).toLocaleDateString();
                return showDate === filterDate;
            }) : showsList;
    
            setShows(filteredShows);
        } catch (err) {
            console.error('Error fetching shows:', err);
        }
    };

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const ticketsRef = collection(firestore, 'tickets');
            const ticketsSnapshot = await getDocs(ticketsRef);

            const ticketsList = await Promise.all(ticketsSnapshot.docs.map(async doc => {
                const ticketData = doc.data();
                if (ticketData.theatreId) {
                    const theatreDoc = await getDoc(doc(firestore, 'theatres', ticketData.theatreId));
                    if (theatreDoc.exists()) {
                        ticketData.theatre = theatreDoc.data();
                    }
                }
                return {
                    id: doc.id,
                    ...ticketData
                };
            }));

            const stats = ticketsList.reduce((acc, ticket) => {
                acc.total++;
                if (ticket.paymentStatus === 'REFUNDED') acc.refunded++;
                else if (ticket.paymentStatus === 'CONFIRMED') acc.confirmed++;
                else acc.pendingTicket++;
                return acc;
            }, { total: 0, refunded: 0, confirmed: 0, pendingTicket: 0 });

            setStats(stats);
            setTickets(ticketsList);
            setError(null);
        } catch (err) {
            setError('Error fetching tickets');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };

            // Reset dependent filters
            if (name === 'district') {
                newFilters.theatreId = '';
                newFilters.screenName = '';
                newFilters.showDate = '';
            } else if (name === 'theatreId') {
                newFilters.screenName = '';
                newFilters.showDate = '';
            } else if (name === 'screenName') {
                newFilters.showDate = '';
            }

            return newFilters;
        });
    };

const [users, setUsers] = useState([]);
    useEffect(() => {
        const fetchData = async () => {
          try {
            const collections = ['Users'];
            const [usersData] = await Promise.all(
              collections.map(async (collectionName) => {
                const snapshot = await getDocs(collection(firestore, collectionName));
                return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              })
            );
    
            
            setUsers(usersData);
        
    
    
            setLoading(false);
          } catch (error) {
            console.error("Error fetching data:", error);
            setLoading(false);
          }
        };
        fetchData();
      }, []);

    const handleBulkAction = async (action) => {
        setActionLoading(true);
        setActionStatus({ message: '', type: '' });

        try {
            for (const ticketId of selectedTickets) {
                const ticket = tickets.find(t => t.id === ticketId);
                if (ticket) {
                    await handleTicketAction(
                        ticket.id,
                        ticket['user-id'],
                        ticket['amount-paid'],
                        action
                    );
                }
            }
            setSelectedTickets([]);
            setActionStatus({
                message: `Bulk ${action.toLowerCase()} completed successfully`,
                type: 'success'
            });
        } catch (err) {
            setActionStatus({
                message: `Error during bulk ${action.toLowerCase()}: ${err.message}`,
                type: 'error'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleTicketAction = async (ticketId, userId, amountPaid, newStatus) => {
        try {
            const ticketRef = doc(firestore, 'tickets', ticketId);
            const userRef = doc(firestore, 'users', userId);

            const ticketDoc = await getDoc(ticketRef);
            if (!ticketDoc.exists()) {
                throw new Error('Ticket not found');
            }

            const ticketData = ticketDoc.data();

            if (ticketData.paymentStatus === newStatus) {
                throw new Error(`Ticket is already ${newStatus.toLowerCase()}`);
            }

            if (newStatus === 'REFUNDED') {
                const userDoc = await getDoc(userRef);
                if (!userDoc.exists()) {
                    throw new Error('User not found');
                }

                await updateDoc(userRef, {
                    redeemPoints: increment(amountPaid),
                    updatedAt: new Date()
                });

                await updateDoc(ticketRef, {
                    paymentStatus: newStatus,
                    refundedAt: new Date(),
                    refundAmount: amountPaid
                });
            } else if (newStatus === 'CONFIRMED') {
                await updateDoc(ticketRef, {
                    paymentStatus: newStatus,
                    confirmedAt: new Date()
                });
            }

            await fetchTickets();
        } catch (err) {
            throw err;
        }
    };


    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedTickets(filteredTickets.map(ticket => ticket.id));
        } else {
            setSelectedTickets([]);
        }
    };

    const handleSelectTicket = (ticketId) => {
        setSelectedTickets(prev => {
            if (prev.includes(ticketId)) {
                return prev.filter(id => id !== ticketId);
            }
            return [...prev, ticketId];
        });
    };

    const filteredTickets = tickets.filter(ticket => {
        return (
            (!filters.paymentStatus || ticket.paymentStatus === filters.paymentStatus) &&
            (!filters.district || (
                theatres.find(theatre => theatre.id === ticket.movie_details['theatre-id'])?.district === filters.district
            )) &&
            (!filters.theatreId || ticket.movie_details['theatre-id'] === filters.theatreId) &&
            (!filters.screenName || ticket.movie_details['screen-name'] === filters.screenName) &&
            (!filters.showDate || (() => {
                const ticketShowDate = ticket.movie_details['show-date'];
                const filterShowDate = filters.showDate;
                
                // Handle Firebase Timestamp or regular date
                const ticketDate = ticketShowDate.toDate ? ticketShowDate.toDate() : new Date(ticketShowDate);
                const compareDate = new Date(filterShowDate);
                
                return ticketDate.toLocaleDateString() === compareDate.toLocaleDateString();
            })())
        );
    });

    const filteredTheatres = theatres.filter(theatre =>
        !filters.district || theatre.district === filters.district
    );

    const uniqueDistricts = [...new Set(theatres.map(theatre => theatre.district))];

    const getStatusColor = (status) => {
        switch (status) {
            case 'REFUNDED':
                return 'bg-red-100 text-red-800';
            case 'UNCONFIRMED':
                return 'bg-yellow-100 text-yellow-800';
            case 'CONFIRMED':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-blue-100 text-blue-800';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <main className="container mx-auto px-4 py-8">
                    <div className="flex justify-center items-center h-64">
                        <div className="text-xl text-gray-600">Loading...</div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-gray-100">
                <Header />
                <main className="container mx-auto px-4 py-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Tickets Management</h2>

                        {actionStatus.message && (
                            <Alert
                                type={actionStatus.type}
                                title={actionStatus.type === 'success' ? 'Success' : 'Error'}
                            >
                                {actionStatus.message}
                            </Alert>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="text-blue-600 text-sm font-medium">Total Tickets</p>
                                <p className="text-2xl font-bold">{stats.total}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <p className="text-green-600 text-sm font-medium">Confirmed Tickets</p>
                                <p className="text-2xl font-bold">{stats.confirmed}</p>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <p className="text-yellow-600 text-sm font-medium">Unconfirmed Tickets</p>
                                <p className="text-2xl font-bold">{stats.pendingTicket}</p>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg">
                                <p className="text-red-600 text-sm font-medium">Refunded Tickets</p>
                                <p className="text-2xl font-bold">{stats.refunded}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                            <div>
                                <select
                                    name="paymentStatus"
                                    value={filters.paymentStatus}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">All Ticket Statuses</option>
                                    <option value="CONFIRMED">Confirmed</option>
                                    <option value="UNCONFIRMED">Unconfirmed</option>
                                    <option value="REFUNDED">Refunded</option>
                                </select>
                            </div>
                            <div>
                                <select
                                    name="district"
                                    value={filters.district}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">All Districts</option>
                                    {uniqueDistricts.map(district => (
                                        <option key={district} value={district}>
                                            {district}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <select
                                    name="theatreId"
                                    value={filters.theatreId}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">All Theatres</option>
                                    {filteredTheatres.map(theatre => (
                                        <option key={theatre.id} value={theatre.id}>
                                            {theatre.owner}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <select
                                    name="screenName"
                                    value={filters.screenName}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    disabled={!filters.theatreId}
                                >
                                    <option value="">All Screens</option>
                                    {screens.map(screen => (
                                        <option key={screen} value={screen}>
                                            {screen}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <input
                                    type="date"
                                    name="showDate"
                                    value={filters.showDate}
                                    onChange={handleFilterChange}
                                    disabled={!filters.theatreId || !filters.screenName}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {selectedTickets.length > 0 && (
                            <div className="mb-4 flex gap-4">
                                <button
                                    onClick={() => handleBulkAction('CONFIRMED')}
                                    disabled={actionLoading}
                                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                >
                                    Confirm Selected
                                </button>
                                <button
                                    onClick={() => handleBulkAction('REFUNDED')}
                                    disabled={actionLoading}
                                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                >
                                    Refund Selected
                                </button>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left">
                                            <input
                                                type="checkbox"
                                                onChange={handleSelectAll}
                                                checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ticket ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Theatre & District
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Show Date & Time
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Seat No
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTickets.map(ticket => (
                                        <tr key={ticket.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTickets.includes(ticket.id)}
                                                    onChange={() => handleSelectTicket(ticket.id)}
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {ticket.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
    {ticket.movie_details['theatre-id'] ? (
        <div>
            <div className="font-medium">
                {
                    theatres.find(theatre => theatre.id === ticket.movie_details['theatre-id'])
                        ?.owner
                }
            </div>
            <div className="text-sm text-gray-500">
                {
                    theatres.find(theatre => theatre.id === ticket.movie_details['theatre-id'])
                        ?.district
                }
            </div>
        </div>
    ) : (
        'N/A'
    )}
</td>

<td className="px-6 py-4 whitespace-nowrap">
    {ticket.movie_details['show-date'] ? (
        (() => {
            // Check if the timestamp is a Firebase Timestamp object
            const timestamp = ticket.movie_details['show-date'];
            
            // Handle Firebase Timestamp
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            
            return isNaN(date.getTime()) ? (
                'Invalid Date'
            ) : (
                <div>
                    <div>{date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div className="text-sm text-gray-500">
                        {date.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true,
                        })}
                    </div>
                </div>
            );
        })()
    ) : (
        'N/A'
    )}
</td>



                                            <td className="px-6 py-4 whitespace-nowrap">
                                            {users.find(u => u.id === ticket['phone'])?.name || 'Unknown'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
    {ticket.seats && ticket.seats.length > 0 
        ? ticket.seats.join(', ') 
        : 'N/A'}
</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                ₹{ticket['amount']}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(ticket.paymentStatus)}`}>
                                                    {ticket.paymentStatus}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {ticket.paymentStatus !== 'REFUNDED' && ticket.paymentStatus !== 'CONFIRMED' && (
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleTicketAction(
                                                                ticket.id,
                                                                ticket['user-id'],
                                                                ticket['amount-paid'],
                                                                'CONFIRMED'
                                                            )}
                                                            disabled={actionLoading}
                                                            className="text-green-600 hover:text-green-900"
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={() => handleTicketAction(
                                                                ticket.id,
                                                                ticket['user-id'],
                                                                ticket['amount-paid'],
                                                                'REFUNDED'
                                                            )}
                                                            disabled={actionLoading}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            Refund
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredTickets.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No tickets found matching the filters
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
            <Footer />
        </>
    );
};

export default TicketsListPage;